import { fieldIsInvalid, fieldIsRequired, getValidationResult } from 'shared/utils/tools'
import * as yup from 'yup'
import uuid from 'uuid/v4'
import map from 'lodash/map'
import range from 'lodash/range'
import random from 'lodash/random'

import { COACH } from 'shared/hocs/withAuth'
import { Identifiable } from 'shared/types'
import { Colors as Scheme } from 'styled-components'

import ParticipantSVG from 'assets/icons/Participants'
import CoachSVG from 'assets/icons/Coaches'
import AdminSVG from 'assets/icons/Admin'
import CoupleSVG from 'assets/icons/Couple'
import VolunteerSVG from 'assets/icons/Volunteer'

const COLORS = [
  '#BD0606',
  '#D35914',
  '#DA9001',
  '#1FA153',
  '#089EA8',
  '#085BA8',
  '#2B3097',
  '#5B2B97',
  '#9216A6',
  '#BB0488',
]

const DUMMY_PERMISSION_LIST = [
  'Can Create Coach profiles',
  'Can Export Coach profiles',
  'Can Create Participant profiles',
  'Can Export Participant profiles',
  'Can View Participant List',
  'Can Check-in/out members ',
  'Can Create Programs ',
  'Can Create Events ',
  'Can Add in Drop In Participants ',
  'Can Create Points',
  'Can Reward Challenge Points',
  'Can Reward Life Skills Points',
  'Can add items to store ',
  'Can Create Survey',
  'Can Export Survey Results',
  'Can View Reports ',
  'Can Create Communications',
  'Can view Schedule',
  'Can view Programs ',
  'Can View Events',
  'Can View Inbox',
  'Can View Challenges',
  'Can View Store ',
]

export const DUMMY_PERMISSION = DUMMY_PERMISSION_LIST.map(each => ({
  id: uuid(),
  name: each,
}))

export const DUMMY_USE_COLORS = ['#D359', '#085BA8', '#BB0488']

export const SVG_ICONS = [
  {
    name: 'Participants',
    Icon: ParticipantSVG,
  },
  {
    name: 'Coaches',
    Icon: CoachSVG,
  },
  {
    name: 'Admin',
    Icon: AdminSVG,
  },
  {
    name: 'Couple',
    Icon: CoupleSVG,
  },
  {
    name: 'Volunteers',
    Icon: VolunteerSVG,
  },
]

export const ICONS = map(SVG_ICONS, 'name')

export const ADMIN_SHADE = {
  black: '#000000',
  border: '#E0E0E0',
  label: '#919191',
  main: '#95358F',
  secondary: '#353535',
  white: '#FFFFFF',
  lightGrey: '#C0C0C0',
  inputBorder: '#DCDCDC',
  textLink: '#1573FF',
  darkMain: '#872B81',
  tableAlternate: '#F9F3F8',
  focusShadow: 'rgba(149, 53, 143, 0.2)',
}

export const THEMES = COLORS.map(code => ({
  code,
  scheme: {
    black: '#000000',
    border: '#E0E0E0',
    label: '#919191',
    main: code,
    secondary: '#353535',
    white: '#FFFFFF',
    lightGrey: '#C0C0C0',
    inputBorder: '#DCDCDC',
    textLink: '#1573FF',
    darkMain: '#872B81',
    tableAlternate: '#F9F3F8',
    focusShadow: 'rgba(149, 53, 143, 0.2)',
  },
}))

export type ThemeType = {
  code: string
  scheme: Scheme
}

export type PermissionType = {
  id: string
  name: string
  node?: string
  node_row?: number
  node_column?: number
}

const colorSchema = yup.object().shape({
  main: yup.string(),
  secondary: yup.string(),
  white: yup.string(),
  black: yup.string(),
  border: yup.string(),
  label: yup.string(),
  lightGrey: yup.string(),
  inputBorder: yup.string(),
  textLink: yup.string(),
  darkMain: yup.string(),
  tableAlternate: yup.string(),
  focusShadow: yup.string(),
})

export const userRoleSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string().required(fieldIsRequired),
  display_name: yup.string(),
  level: yup
    .number()
    .notRequired()
    .nullable(),
  icon: yup.string().required(fieldIsRequired),
  permission_ids: yup
    .array()
    .of(yup.string())
    .min(1)
    .required((params: any) => fieldIsRequired(params).replace(/Ids/g, '')),
  scheme: colorSchema
    .required(fieldIsRequired)
    .transform(value => (value === null ? ADMIN_SHADE : value))
    .default(ADMIN_SHADE),
})

export type UserRoleType = yup.InferType<typeof userRoleSchema>
export type UserRoleTypeWithStatus = yup.InferType<typeof userRoleSchema> & {
  status?: string
  // level?: number
  unsigned_waivers_count?: {
    All: number
    Active: number
    Inactive: number
    [k: string]: any | undefined
  }
  can_be_deleted?: boolean
  is_default?: boolean
  default_permission_ids?: string[]
}

export function getEmptyUserRole(name?: string): UserRoleTypeWithStatus {
  return {
    id: uuid(),
    name: name || '',
    display_name: name || '',
    icon: '',
    level: COACH,
    scheme: ADMIN_SHADE,
    permission_ids: [],
  }
}

export default function validator(data: UserRoleType) {
  return getValidationResult(data, userRoleSchema)
}

export function getDummyRoles(): UserRoleTypeWithStatus[] {
  return ['Admin', 'Coach', 'Manager'].map(each => {
    const permissions = DUMMY_PERMISSION.filter((each: any, index: number) => {
      return range(random(2, 7))
        .map(e => random(0, 22))
        .includes(index)
    })

    return {
      ...getEmptyUserRole(each),
      permission_ids: map(permissions, 'id'),
    }
  })
}

/*
  [].reduce((result, each) => {
    return `${result} \n export const ${each.name.trim().replace(/[^a-zA-Z0-9]/g,'_').toUpperCase()} = ${each.id}`
  }, '')
*/
